import React from "react";
import { MessageCircle, MapPin, Phone, Mail, ChevronRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionLabel from "@/components/SectionLabel";
import {
  BAH_ADDRESS_LINES,
  BAH_EMAIL,
  BAH_PHONE_DISPLAY,
  BAH_WHATSAPP_DISPLAY,
  WA_TEMPLATES,
  buildWaLink,
} from "@/lib/whatsapp";

const ROUTER = [
  { id: "vet", label: "I am a veterinary professional", message: WA_TEMPLATES.vet },
  { id: "agrovet", label: "I run an agrovet", message: WA_TEMPLATES.agrovet },
  { id: "farmer", label: "I am a farmer", message: WA_TEMPLATES.farmer },
  { id: "product_info", label: "I need product information", message: WA_TEMPLATES.product_info },
  { id: "instruments", label: "I need instruments", message: WA_TEMPLATES.instruments },
  { id: "cold_chain", label: "I need cold-chain / vaccine support", message: WA_TEMPLATES.cold_chain },
  { id: "training", label: "I want training / CMT", message: WA_TEMPLATES.training },
  { id: "stockist", label: "I want to become a stockist", message: WA_TEMPLATES.stockist },
  { id: "distribution", label: "I am a distributor / institution", message: WA_TEMPLATES.distribution },
];

export default function Contact() {
  return (
    <div data-testid="contact-page">
      <PageHero
        testId="contact-hero"
        eyebrow="Contact / WhatsApp Support"
        title="Tell us what you need — we'll route you to the right team."
        lede="Veterinary professionals, agrovets, farmers, distributors and institutions — pick the option that fits, and we'll open a WhatsApp conversation with the right Biodeal Animal Health team."
        image="https://images.pexels.com/photos/6235117/pexels-photo-6235117.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=1600"
        primaryCta={{
          label: "WhatsApp Technical Team",
          href: buildWaLink(WA_TEMPLATES.technical_general + " "),
          external: true,
        }}
        secondaryCta={{
          label: `Call ${BAH_PHONE_DISPLAY}`,
          href: "tel:+254706333829",
        }}
      />

      {/* Support router */}
      <section className="py-20 md:py-28 bg-bah-paper" data-testid="contact-router-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <SectionLabel>Support router</SectionLabel>
            <h2 className="mt-4 font-display text-3xl md:text-5xl text-bah-ink leading-tight">
              What do you need help with?
            </h2>
            <p className="mt-4 text-bah-mute md:text-lg">
              Each option opens a WhatsApp conversation with the right Biodeal Animal Health team — with the right message already pre-filled.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {ROUTER.map((r) => (
              <a
                key={r.id}
                href={buildWaLink(r.message)}
                target="_blank"
                rel="noopener noreferrer"
                className="group card-rise rounded-2xl bg-white border border-bah-line p-6 flex items-start justify-between gap-4"
                data-testid={`router-${r.id}`}
              >
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-bah-green font-bold">
                    {r.id.replace("_", " ")}
                  </div>
                  <div className="mt-2 font-display text-lg text-bah-ink leading-snug">
                    {r.label}
                  </div>
                  <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#1FBE5A]">
                    Open WhatsApp <ChevronRight className="h-3.5 w-3.5" />
                  </div>
                </div>
                <div className="h-10 w-10 rounded-full bg-[#25D366]/10 text-[#1FBE5A] grid place-content-center shrink-0">
                  <MessageCircle className="h-5 w-5" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Office details */}
      <section className="py-20 md:py-28 bg-bah-ink text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-6">
            <div className="eyebrow !text-bah-yellow">Visit / Write / Call</div>
            <h2 className="mt-4 font-display text-3xl md:text-5xl leading-tight">
              Biodeal Animal Health — Nairobi, Kenya.
            </h2>
            <p className="mt-5 text-white/75 md:text-lg max-w-md">
              Part of Biodeal Laboratories Limited. Our team is reachable by
              phone, WhatsApp, email — or in person at our Nairobi office.
            </p>
          </div>
          <div className="lg:col-span-6">
            <ul className="space-y-5">
              <li className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg bg-bah-yellow/20 text-bah-yellow grid place-content-center shrink-0">
                  <MapPin className="h-5 w-5" />
                </div>
                <div className="text-sm leading-relaxed">
                  {BAH_ADDRESS_LINES.map((line, i) => (
                    <div key={i}>{line}</div>
                  ))}
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg bg-bah-yellow/20 text-bah-yellow grid place-content-center shrink-0">
                  <Phone className="h-5 w-5" />
                </div>
                <a href="tel:+254706333829" className="text-base hover:text-bah-yellow" data-testid="contact-phone">
                  {BAH_PHONE_DISPLAY}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg bg-[#25D366]/20 text-[#25D366] grid place-content-center shrink-0">
                  <MessageCircle className="h-5 w-5" />
                </div>
                <a
                  href={buildWaLink("Hello Biodeal Animal Health")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-base hover:text-bah-yellow"
                  data-testid="contact-whatsapp"
                >
                  {BAH_WHATSAPP_DISPLAY} — WhatsApp
                </a>
              </li>
              <li className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg bg-bah-yellow/20 text-bah-yellow grid place-content-center shrink-0">
                  <Mail className="h-5 w-5" />
                </div>
                <a href={`mailto:${BAH_EMAIL}`} className="text-base hover:text-bah-yellow" data-testid="contact-email">
                  {BAH_EMAIL}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
